import type { Conversation } from '@auw/workspace-utils';
import { getActiveAccountId } from '@auw/api-client';
import { trackWorkspaceBootstrapCache } from '@auw/workspace-utils';
import { activeTenantKey as tenantKey } from '@auw/tenant';

export const FAST_BOOTSTRAP_RECENT_LIMIT = 8;
const WORKSPACE_BOOTSTRAP_CACHE_KEY_PREFIX = tenantKey('workspace_bootstrap_cache');
const WORKSPACE_BOOTSTRAP_CACHE_VERSION = 1;
const WORKSPACE_BOOTSTRAP_CACHE_TTL_MS = 15 * 60_000;

export type BootstrapLoadResult = {
  conversations: Record<string, Conversation>;
  order: string[];
  workspaceOrder: string[];
  count: number;
};

type WorkspaceBootstrapCachePayload = BootstrapLoadResult & {
  v: number;
  ts: number;
};

function getWorkspaceBootstrapCacheKey(): string | null {
  if (typeof window === 'undefined') return null;
  const accountId = getActiveAccountId();
  if (!accountId) return null;
  return `${WORKSPACE_BOOTSTRAP_CACHE_KEY_PREFIX}:${accountId}`;
}

function compactBootstrapResult(result: BootstrapLoadResult): BootstrapLoadResult {
  const seedOrder = result.order.length > 0 ? result.order : result.workspaceOrder;
  const candidateIds = seedOrder.slice(0, FAST_BOOTSTRAP_RECENT_LIMIT);
  if (candidateIds.length === 0) {
    return { conversations: {}, order: [], workspaceOrder: [], count: result.count };
  }

  const conversations: Record<string, Conversation> = {};
  for (const id of candidateIds) {
    const conversation = result.conversations[id];
    if (!conversation) continue;
    conversations[id] = {
      ...conversation,
      messages: [],
      historyLoaded: false,
    };
  }

  const existingIds = new Set(Object.keys(conversations));
  const order = candidateIds.filter((id) => existingIds.has(id));
  const workspaceOrder = result.workspaceOrder.filter((id) => existingIds.has(id));
  return {
    conversations,
    order,
    workspaceOrder: workspaceOrder.length > 0 ? workspaceOrder : order,
    count: result.count,
  };
}

export function readWorkspaceBootstrapCache(): BootstrapLoadResult | null {
  const key = getWorkspaceBootstrapCacheKey();
  if (!key) {
    trackWorkspaceBootstrapCache('miss', { reason: 'no_key' });
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      trackWorkspaceBootstrapCache('miss', { reason: 'no_entry' });
      return null;
    }
    const parsed = JSON.parse(raw) as WorkspaceBootstrapCachePayload;
    if (!parsed || parsed.v !== WORKSPACE_BOOTSTRAP_CACHE_VERSION) {
      trackWorkspaceBootstrapCache('miss', { reason: 'version_mismatch' });
      return null;
    }
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > WORKSPACE_BOOTSTRAP_CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      trackWorkspaceBootstrapCache('miss', { reason: 'expired' });
      return null;
    }
    if (!parsed.conversations || typeof parsed.conversations !== 'object') {
      trackWorkspaceBootstrapCache('miss', { reason: 'invalid_payload' });
      return null;
    }

    const compact = compactBootstrapResult({
      conversations: parsed.conversations,
      order: Array.isArray(parsed.order) ? parsed.order : [],
      workspaceOrder: Array.isArray(parsed.workspaceOrder) ? parsed.workspaceOrder : [],
      count: typeof parsed.count === 'number' ? parsed.count : 0,
    });

    if (Object.keys(compact.conversations).length === 0) {
      trackWorkspaceBootstrapCache('miss', { reason: 'empty' });
      return null;
    }

    trackWorkspaceBootstrapCache('hit', { count: Object.keys(compact.conversations).length });
    return compact;
  } catch {
    trackWorkspaceBootstrapCache('miss', { reason: 'parse_error' });
    return null;
  }
}

export function writeWorkspaceBootstrapCache(result: BootstrapLoadResult): void {
  const key = getWorkspaceBootstrapCacheKey();
  if (!key) return;
  const compact = compactBootstrapResult(result);
  if (Object.keys(compact.conversations).length === 0) return;
  try {
    const payload: WorkspaceBootstrapCachePayload = {
      v: WORKSPACE_BOOTSTRAP_CACHE_VERSION,
      ts: Date.now(),
      ...compact,
    };
    window.sessionStorage.setItem(key, JSON.stringify(payload));
    trackWorkspaceBootstrapCache('write', { count: Object.keys(compact.conversations).length });
  } catch {
    // sessionStorage quota/deny는 무시
  }
}
